<template>
  <div class="overflow-y-hidden d-flex flex-column">
    <v-row no-gutters class="pb-2">
      <v-col class="grow">
        <h2 v-if="!ready || !subSections.length">{{ $t('analysis.sectionTabs.sectionIntroduction') }}</h2>
      </v-col>
      <v-col v-if="!subSections.length" class="shrink d-flex">
        <platform-button
          v-if="aiSectionIntro"
          secondary
          default-case
          icon="robot-outline"
          class="mr-2"
          @click="showMainSectionAiDialog"
        >
          {{ $t('analysis.ai.insertContentAction') }}
        </platform-button>
        <report-context-button
          :value="reportContextButtonKey"
          :master-section-id="section.masterSectionId"
          :section="section"
          :report-section="reportSection"
          @insertRecommendation="(content) => handleInsertRecommendedContent(content)"
        />
      </v-col>
    </v-row>
    <platform-spinner
      v-if="loading || !ready"
      class="mt-4 mx-auto"
    />
    <template v-else-if="reportSection && !subSections.length">
      <PlatformWYSIWYG
        :placeholder="tooltip"
        :max-length="textFieldLimit"
        :initial-data="currentContent"
        justified
        @newInput="saveUpdate"
      />
      <div class="text-right pt-2">
        <sign-off-button
          :value="sectionIntroReportResult && !!sectionIntroReportResult.reviewedAt"
          @input="toggleSectionIntroReviewed"
        />
      </div>
    </template>
    <div v-else-if="!!subSections.length">
      <div v-for="subSection in subSections" :key="subSection.slug" class="mb-10">
        <v-row no-gutters class="pb-2">
          <v-col class="grow">
            <h2>{{ subSection.name }} {{ $t('analysis.introduction.title') }}</h2>
          </v-col>
          <v-col class="shrink pb- d-flex">
            <platform-button
              v-if="getAiSubSectionIntro(subSection.slug)"
              secondary
              default-case
              icon="robot-outline"
              class="mr-2"
              @click="showAiSubSectionDialog(subSection.slug)"
            >
              {{ $t('analysis.ai.insertContentAction') }}
            </platform-button>
            <report-context-button
              :value="reportContextButtonKey"
              :master-section-id="section.masterSectionId"
              :criteria-id="subSection.slug"
              :section="section"
              :sub-section="subSection"
              :report-section="reportSection"
              @insertRecommendation="(content) => handleInsertRecommendedContent(content, subSection.slug)"
            />
          </v-col>
        </v-row>
        <PlatformWYSIWYG
          :placeholder="tooltip"
          :max-length="getSubSectionTextFieldLimit(subSection.slug)"
          :initial-data="getCurrentContentBySlug(subSection.slug)"
          justified
          @newInput="(value) => saveSubSectionUpdate(subSection.slug, { value })"
        />
        <div class="text-right pt-2">
          <sign-off-button
            :value="!!subSection.reviewedAt"
            @input="toggleSubSectionIntroReviewed(subSection.slug, subSection.reviewedAt)"
          />
        </div>
      </div>
    </div>

    <!-- AI Section Introduction Confirmation Dialog -->
    <platform-modal
      v-model="showAiConfirmDialog"
      :title="$t('analysis.ai.useAiContentTitle')"
      :width="600"
      icon="robot-outline"
      @close="currentSubSectionSlug = null"
    >
      <p>{{ $t('analysis.ai.replaceSectionIntroInstruction') }}</p>
      <div
        class="grey lighten-4 rounded mt-3 pa-3 pr-5 overflow-y-auto"
        :style="{ maxHeight: '300px' }"
      >
        <p class="text-body-2 mb-0 text-justify">{{ displayedAiContent }}</p>
      </div>
      <p class="mt-3 text-caption warning--text">
        {{ $t('analysis.ai.replaceWarning') }}
      </p>
      <template #actions>
        <platform-button
          primary
          @click="useAiContent"
        >
          {{ $t('analysis.ai.replaceWithContentAction') }}
        </platform-button>
      </template>
    </platform-modal>
  </div>
</template>

<script lang="ts">
import ReportContextButton from '@/components/analysis/ReportContextButton.vue'
import SignOffButton from '@/components/analysis/SignOffButton.vue'
import PlatformSpinner from '@/components/shared/PlatformSpinner.vue'
import PlatformButton from '@/components/shared/PlatformButton.vue'
import PlatformModal from '@/components/PlatformModal.vue'
import { PlatformReport, PlatformReportSection, ReportSectionField, ReportSectionResult } from '@/types/Report'
import Vue, { PropType } from 'vue'
import { SubSectionIntroduction } from '@/API'
import { MaxFieldLengths } from '@/helpers/validation'
import PlatformWYSIWYG from '@/components/PlatformWYSIWYG.vue'
import { ReportContextItem } from '@/helpers/recommendations'
import { SectionResults } from '@betterboards/shared/types/ResultSet'
import { parseMarkdownContent } from '@/helpers/text'
import { InputFields } from '@betterboards/shared/types/Platform'

export default Vue.extend({
  components: {
    SignOffButton,
    ReportContextButton,
    PlatformWYSIWYG,
    PlatformSpinner,
    PlatformButton,
    PlatformModal
  },
  props: {
    section: Object as PropType<SectionResults>,
    survey: Object,
    responses: Array,
    reportSection: Object as PropType<PlatformReportSection>,
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      subSectionIntroductions: <SubSectionIntroduction[]>[],
      MaxFieldLengths,
      InputFields,
      showAiConfirmDialog: false,
      currentSubSectionSlug: null as string | null
    }
  },
  async mounted (): Promise<void> {
    this.initSubSections()
  },
  watch: {
    subSections: 'initSubSections'
  },
  computed: {
    aiContent (): any {
      return this.$store.state.Analysis.aiContent
    },
    ready (): boolean {
      if (this.subSections.length) {
        // Has SubSections data
        return true
      }
      // Has ReportSection
      return !!this.reportSection
    },
    aiSectionIntro (): string | undefined {
      if (!this.aiContent?.groupBySection || !this.section?.masterSectionId) {
        return undefined
      }

      // Look for ROOT entry when no subsections
      const rootKey = `${this.section.masterSectionId}::ROOT`
      const sectionData = this.aiContent.groupBySection[rootKey]

      if (!sectionData) {
        return undefined
      }

      const parts: string[] = []

      if (sectionData.sectionIntroduction) {
        // parts.push(`**${this.$t('analysis.introduction.title')}**` as string)
        parts.push(sectionData.sectionIntroduction)
      }

      // Commented out section summary - keeping only introduction
      // if (sectionData.sectionSummary) {
      //   parts.push(`**${this.$t('analysis.summary.title')}**` as string)
      //   parts.push(sectionData.sectionSummary)
      // }

      return parts.length > 0 ? parts.join('\n\n') : undefined
    },
    selectedReport (): PlatformReport | null {
      return this.$store.getters['Report/selectedReport']
    },
    subSections (): { name: string, slug: string, reviewedAt?: string }[] {
      if (!this.section?.averages?.subSections?.length) {
        return []
      }
      return this.section.averages.subSections.map((subSection) => {
        const ssIntro = this.reportSection?.subSectionIntroductions?.find((ss) => ss && ss.slug === subSection.slug)
        return {
          name: subSection.name,
          slug: subSection.slug,
          reviewedAt: ssIntro?.reviewedAt ?? undefined
        }
      })
    },
    currentContent: {
      get (): string | undefined {
        if (this.reportSection?.introduction && this.reportSection?.introduction.length > 0) {
          return this.reportSection.introduction
        }

        return undefined
      },
      set (val: string | undefined): void {
        if (this.reportSection) {
          const reportSection = {
            ...this.reportSection,
            introduction: val
          }
          this.$emit('updateReportSection', reportSection)
        }
      }
    },
    sectionIntroReportResult (): ReportSectionField | undefined {
      return this.reportSection.results.find((r: ReportSectionResult) => {
        if (r.questionId) {
          return false
        }
        const reportSectionFieldResult = r as ReportSectionField
        return reportSectionFieldResult.field === 'section-intro'
      }) as ReportSectionField | undefined
    },
    tooltip (): string {
      return this.$t('analysis.introduction.tooltip') as string
    },
    reportContextButtonKey (): ReportContextItem.SectionIntroduction {
      return ReportContextItem.SectionIntroduction
    },
    textFieldLimit (): number {
      const sectionIntroFieldOverride: number | undefined = this.selectedReport?.configuration?.maxFieldLengths?.sections?.[this.section.id]?.[InputFields.SectionIntroduction]
      if (sectionIntroFieldOverride) {
        return sectionIntroFieldOverride
      }
      return MaxFieldLengths[InputFields.SectionIntroduction]
    },
    displayedAiContent (): string | undefined {
      if (this.currentSubSectionSlug) {
        return this.getAiSubSectionIntro(this.currentSubSectionSlug)
      }
      return this.aiSectionIntro
    }
  },
  methods: {
    initSubSections (): void {
      if (this.reportSection?.subSectionIntroductions) {
        this.subSectionIntroductions = this.reportSection?.subSectionIntroductions as SubSectionIntroduction[]
      }
    },
    getCurrentContentBySlug (slug: string): string | null | undefined {
      if (!this.subSectionIntroductions?.length) {
        return
      }
      const item = this.subSectionIntroductions.find((item: any) => item.slug === slug)
      if (!item) {
        return ''
      }
      return item.value
    },
    saveUpdate (val): void {
      if (!this.reportSection) {
        console.error('Unable to save section introduction as no report section is loaded')
        return
      }
      this.currentContent = val
    },
    saveSubSectionUpdate (slug: string, val: Pick<SubSectionIntroduction, 'value' | 'reviewedAt'>): void {
      if (!this.reportSection) {
        console.error('Unable to save sub-section introduction as no report section is loaded')
        return
      }
      const subSectionIntroductions = this.subSectionIntroductions?.length
        ? this.subSectionIntroductions
        : []
      const index = subSectionIntroductions.findIndex((item: any) => item.slug === slug)
      if (index === -1) {
        subSectionIntroductions.push({ slug, ...val } as any)
      } else {
        subSectionIntroductions.splice(index, 1, {
          ...subSectionIntroductions[index],
          ...val
        })
      }
      const reportSection = {
        ...this.reportSection,
        subSectionIntroductions
      }
      this.$emit('updateReportSection', reportSection)
    },
    toggleSectionIntroReviewed (): void {
      const results = this.reportSection.results
      const index = results.findIndex((r: ReportSectionResult) => {
        if (r.questionId) {
          return false
        }
        const reportSectionFieldResult = r as ReportSectionField
        return reportSectionFieldResult.field === 'section-intro'
      })
      if (index === -1) {
        results.push({
          field: 'section-intro',
          reviewedAt: (new Date()).toISOString()
        } as ReportSectionField)
      } else {
        results.splice(index, 1, {
          ...results[index],
          reviewedAt: results[index].reviewedAt ? null : (new Date()).toISOString()
        } as ReportSectionField)
      }
      const reportSection = {
        ...this.reportSection,
        results
      }
      this.$emit('updateReportSection', reportSection)
    },
    toggleSubSectionIntroReviewed (slug: string, currentReviewedStatus?: boolean): void {
      this.saveSubSectionUpdate(slug, { reviewedAt: currentReviewedStatus ? null : (new Date()).toISOString() })
    },
    handleInsertRecommendedContent (content: string, subSectionSlug?: string | undefined): void {
      if (subSectionSlug) {
        const subSectionIntroductions = this.subSectionIntroductions
        const currentSubSectionIntro: SubSectionIntroduction | undefined = subSectionIntroductions.find(
          (item: SubSectionIntroduction) => item.slug === subSectionSlug
        ) ?? undefined
        const newValue = currentSubSectionIntro?.value
          ? `${currentSubSectionIntro.value}\n\n${content}`
          : content
        const newSubSectionIntro: Partial<SubSectionIntroduction> = {
          ...currentSubSectionIntro,
          value: parseMarkdownContent(newValue)
        }
        this.saveSubSectionUpdate(subSectionSlug, newSubSectionIntro)
        return
      }
      const newContent = this.currentContent
        ? `${this.currentContent}\n\n${content}`
        : content
      this.saveUpdate(
        parseMarkdownContent(newContent)
      )
    },
    getSubSectionTextFieldLimit (subSectionSlug: string): number {
      const subSectionIntroFieldOverride: number | undefined = this.selectedReport?.configuration?.maxFieldLengths?.sections?.[subSectionSlug]?.[InputFields.SectionIntroductionCompact]
      if (subSectionIntroFieldOverride) {
        return subSectionIntroFieldOverride
      }
      const sectionIntroFieldOverride: number | undefined = this.selectedReport?.configuration?.maxFieldLengths?.sections?.[this.section.id]?.[InputFields.SectionIntroductionCompact]
      if (sectionIntroFieldOverride) {
        return sectionIntroFieldOverride
      }
      return MaxFieldLengths[InputFields.SectionIntroductionCompact]
    },
    getAiSubSectionIntro (subSectionSlug: string): string | undefined {
      if (!this.aiContent?.groupBySection || !this.section?.masterSectionId) {
        return undefined
      }

      // Look for specific subsection entry
      const subSectionKey = `${this.section.masterSectionId}::${subSectionSlug}`
      const subSectionData = this.aiContent.groupBySection[subSectionKey]

      if (subSectionData?.sectionIntroduction) {
        return subSectionData.sectionIntroduction
      }

      return undefined
    },
    showMainSectionAiDialog (): void {
      if (!this.aiSectionIntro) return
      this.currentSubSectionSlug = null
      this.showAiConfirmDialog = true
    },
    showAiSubSectionDialog (subSectionSlug: string): void {
      const aiContent = this.getAiSubSectionIntro(subSectionSlug)
      if (!aiContent) return
      this.currentSubSectionSlug = subSectionSlug
      this.showAiConfirmDialog = true
    },
    useAiContent (): void {
      if (!this.displayedAiContent) return

      if (this.currentSubSectionSlug) {
        // Handle subsection - use existing method
        this.handleInsertRecommendedContent(this.displayedAiContent, this.currentSubSectionSlug)
      } else {
        // Handle main section - let the editor's CSS default handle justification
        const formattedContent = parseMarkdownContent(this.displayedAiContent)
        this.saveUpdate(formattedContent)
      }

      this.showAiConfirmDialog = false
      this.currentSubSectionSlug = null
      this.$toasted.success(this.$t('analysis.aiAppliedToast') as string)
    }
  }
})
</script>

<style lang="scss" scoped>
.text-justify {
  text-align: justify;
}
</style>
